import React, { useEffect, useRef, useState } from 'react';
import Select, { MultiValue } from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { AiManagementFilters, AiManagementState } from 'store/modules/ai_management_store/types';
import { setFilters } from 'store/modules/ai_management_store/actions';
import { Panel } from './styles';

interface AiManagementFiltersPanelProps {
  onClose: () => void;
}

interface MemberOption {
  value: number;
  label: string;
}

const DEBOUNCE_MS = 350;
const BOTTOM_GAP = 12;

const AiManagementFiltersPanel: React.FC<AiManagementFiltersPanelProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const { team, filters } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );

  const [memberIds, setMemberIds] = useState<number[]>(filters.memberIds);
  const [scope, setScope] = useState<'mine' | null>(filters.scope);
  const [search, setSearch] = useState(filters.q);
  const [deadlineFrom, setDeadlineFrom] = useState(filters.deadlineFrom ?? '');
  const [deadlineTo, setDeadlineTo] = useState(filters.deadlineTo ?? '');

  const panelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined);
  const isFirstRun = useRef(true);
  const hasPendingRef = useRef(false);

  const currentFilters: AiManagementFilters = {
    memberIds,
    scope,
    q: search,
    deadlineFrom: deadlineFrom || null,
    deadlineTo: deadlineTo || null,
  };
  const latestRef = useRef(currentFilters);
  latestRef.current = currentFilters;

  useEffect(() => {
    const updateHeight = () => {
      if (!panelRef.current) return;
      const { top } = panelRef.current.getBoundingClientRect();
      setPanelHeight(window.innerHeight - top - BOTTOM_GAP);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return undefined;
    }
    hasPendingRef.current = true;
    const timer = setTimeout(() => {
      dispatch(setFilters(latestRef.current));
      hasPendingRef.current = false;
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [memberIds, scope, search, deadlineFrom, deadlineTo, dispatch]);

  useEffect(
    () => () => {
      if (hasPendingRef.current) dispatch(setFilters(latestRef.current));
    },
    [dispatch],
  );

  const memberOptions: MemberOption[] = team.map((member) => ({
    value: member.id,
    label: member.name,
  }));
  const selectedMembers = memberOptions.filter((option) => memberIds.includes(option.value));

  const handleMembersChange = (selected: MultiValue<MemberOption>) => {
    setScope(null);
    setMemberIds(selected.map((option) => option.value));
  };

  const selectAll = () => {
    setScope(null);
    setMemberIds([]);
  };

  return (
    <Panel ref={panelRef} panelHeight={panelHeight}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="filters-title font-weight-bold mb-0">Filtros</h5>
        <button
          type="button"
          className="btn btn-link p-0 text-dark d-flex align-items-center"
          aria-label="Fechar"
          onClick={onClose}
        >
          <i className="material-icons">close</i>
        </button>
      </div>

      <div className="mb-3">
        <div className="filters-label mb-1">Buscar licitação</div>
        <div className="filters-input-box d-flex align-items-center pr-2">
          <input
            type="text"
            className="form-control"
            placeholder="Edital ou palavra-chave"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-link p-0 text-primary flex-shrink-0"
            onClick={() => setSearch('')}
          >
            Limpar
          </button>
        </div>
      </div>

      <div className="mb-3">
        <div className="filters-label mb-1">Licitações selecionadas por:</div>
        <Select<MemberOption, true>
          isMulti
          closeMenuOnSelect={false}
          options={memberOptions}
          value={selectedMembers}
          onChange={handleMembersChange}
          placeholder={scope === 'mine' ? 'Por mim' : 'Todos da equipe'}
          noOptionsMessage={() => 'Nenhum membro no quadro'}
          classNamePrefix="react-select"
        />
        <div className="mt-1">
          <button type="button" className="btn btn-link p-0" onClick={selectAll}>
            Todos da equipe
          </button>
        </div>
      </div>

      <div>
        <div className="filters-label mb-1">Prazo</div>
        <div className="filters-daterange d-flex align-items-center">
          <input
            type="date"
            className="form-control"
            aria-label="Prazo de"
            value={deadlineFrom}
            onChange={(event) => setDeadlineFrom(event.target.value)}
          />
          <span className="mx-2 text-muted">-</span>
          <input
            type="date"
            className="form-control"
            aria-label="Prazo até"
            value={deadlineTo}
            onChange={(event) => setDeadlineTo(event.target.value)}
          />
        </div>
      </div>
    </Panel>
  );
};

export default AiManagementFiltersPanel;
